import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";

const COMPANY = "VR Custom Solutions";
const CITY = "Chennai, Tamil Nadu";

const EFFECTIVE_DATE = new Date().toLocaleDateString("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const SECTIONS = [
  {
    heading: "1. About Us",
    body: (
      <>
        <p>
          {COMPANY} specializes in custom surface design solutions, including but not limited to:
        </p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Custom Wallpapers</li>
          <li>Wall Art</li>
          <li>Decorative Window Films</li>
          <li>Privacy Graphics</li>
          <li>Wardrobe Laminations</li>
          <li>Ceiling Graphics</li>
          <li>Architectural Surface Films</li>
          <li>Interior Branding Solutions</li>
          <li>Other bespoke surface applications</li>
        </ul>
        <p className="mt-3">All products and services are created based on individual client requirements.</p>
      </>
    ),
  },
  {
    heading: "2. Website Use",
    body: (
      <>
        <p>You agree to use this website only for lawful purposes. You must not:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Use the website for fraudulent or unlawful activities.</li>
          <li>Attempt to gain unauthorized access to the website or its systems.</li>
          <li>Introduce viruses, malware, or harmful code.</li>
          <li>Interfere with the operation or security of the website.</li>
          <li>Copy, reproduce, or distribute website content without prior written permission.</li>
        </ul>
        <p className="mt-3">We reserve the right to suspend or terminate access to users who violate these Terms.</p>
      </>
    ),
  },
  {
    heading: "3. Intellectual Property",
    body: (
      <>
        <p>
          All content on this website — including text, graphics, images, logos, layouts, icons, designs, and other
          materials — is owned by or licensed to {COMPANY} and is protected by applicable intellectual property laws.
        </p>
        <p className="mt-3">
          You may not reproduce, modify, distribute, or commercially exploit any content without our prior written consent.
        </p>
      </>
    ),
  },
  {
    heading: "4. Custom Design Services",
    body: (
      <>
        <p>
          Our company creates original, customized surface designs tailored to each client&rsquo;s requirements.
        </p>
        <p className="mt-3">Unless otherwise agreed in writing:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Designs are developed specifically for the requesting client.</li>
          <li>Design concepts remain the intellectual property of {COMPANY} until full payment has been received.</li>
          <li>
            We reserve the right to showcase completed projects in our portfolio, website, or marketing materials unless a
            confidentiality agreement states otherwise.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: "5. Quotations and Pricing",
    body: (
      <ul className="list-disc list-outside pl-5 space-y-1">
        <li>All quotations are provided based on the information available at the time of preparation.</li>
        <li>
          Prices are subject to change if project specifications, quantities, materials, or scope of work are modified.
        </li>
        <li>Quotations remain valid only for the period stated in the quotation.</li>
      </ul>
    ),
  },
  {
    heading: "6. Orders and Project Approval",
    body: (
      <>
        <p>Before production begins:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Clients are responsible for reviewing and approving all artwork, dimensions, colors, and specifications.</li>
          <li>Once final approval has been given, production will commence.</li>
          <li>Any changes requested after approval may result in additional costs and revised delivery timelines.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "7. Installation",
    body: (
      <>
        <p>Where installation services are provided:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Clients must ensure the site is ready for installation.</li>
          <li>Delays caused by site conditions, restricted access, or other external factors may affect project schedules.</li>
          <li>Installation timelines are estimates and may change due to unforeseen circumstances.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "8. Product Representation",
    body: (
      <>
        <p>We strive to display colors, textures, and finishes as accurately as possible. However:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>
            Actual colors may vary depending on screen settings, lighting conditions, printing processes, and material
            characteristics.
          </li>
          <li>Slight variations between digital previews and finished products are considered normal.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "9. Limitation of Liability",
    body: (
      <>
        <p>To the maximum extent permitted by law:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>
            We are not liable for indirect, incidental, consequential, or special damages arising from the use of our
            website or services.
          </li>
          <li>
            Our total liability relating to any project shall not exceed the amount paid for the specific service or product.
          </li>
        </ul>
        <p className="mt-3">Nothing in these Terms limits liability where such limitation is prohibited by applicable law.</p>
      </>
    ),
  },
  {
    heading: "10. Third-Party Links",
    body: (
      <>
        <p>Our website may include links to external websites for your convenience.</p>
        <p className="mt-3">
          We do not control or endorse the content, policies, or practices of third-party websites and are not responsible
          for their content or services.
        </p>
      </>
    ),
  },
  {
    heading: "11. Privacy",
    body: (
      <p>
        Your use of this website is also governed by our Privacy Policy, which explains how we collect, use, and protect
        your personal information.
      </p>
    ),
  },
  {
    heading: "12. Availability of Website",
    body: (
      <>
        <p>We aim to keep our website available and up to date but do not guarantee uninterrupted access.</p>
        <p className="mt-3">We reserve the right to:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>Modify website content</li>
          <li>Update services</li>
          <li>Suspend or discontinue portions of the website</li>
          <li>Perform maintenance without prior notice</li>
        </ul>
      </>
    ),
  },
  {
    heading: "13. User Submissions",
    body: (
      <>
        <p>If you submit project details, design ideas, images, or other materials through our website:</p>
        <ul className="mt-2 list-disc list-outside pl-5 space-y-1">
          <li>You confirm that you have the necessary rights to share such materials.</li>
          <li>
            You grant us permission to use them solely for evaluating, designing, quoting, or completing your requested
            project.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: "14. Governing Law",
    body: (
      <>
        <p>
          These Terms &amp; Conditions shall be governed by and interpreted in accordance with the laws of India.
        </p>
        <p className="mt-3">
          Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the competent courts
          located in {CITY}.
        </p>
      </>
    ),
  },
  {
    heading: "15. Changes to These Terms",
    body: (
      <>
        <p>We may update these Terms &amp; Conditions from time to time.</p>
        <p className="mt-3">
          The revised version will become effective immediately upon publication on this website. Continued use of the
          website after any updates constitutes acceptance of the revised Terms.
        </p>
      </>
    ),
  },
  {
    heading: "16. Contact Us",
    body: (
      <>
        <p>If you have any questions regarding these Terms &amp; Conditions, please contact us.</p>
        <div className="mt-3 space-y-1 text-stone-900">
          <p>{COMPANY}</p>
          <p>
            Address: <span className="text-stone-700">Chennai &nbsp;·&nbsp; Coimbatore &nbsp;·&nbsp; Bangalore, India</span>
          </p>
          <p>
            Phone:{" "}
            <a href="tel:+919566413125" className="link-underline text-stone-900">
              +91 95664 13125
            </a>
          </p>
          <p>
            Email:{" "}
            <a href="mailto:admin@varnella.in" className="link-underline text-stone-900">
              admin@varnella.in
            </a>
          </p>
          <p>
            Website:{" "}
            <a href="https://varnella.in" className="link-underline text-stone-900">
              varnella.in
            </a>
          </p>
        </div>
      </>
    ),
  },
];

export default function TermsDialog({ trigger, className = "" }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button
            data-testid="terms-trigger"
            className={className || "link-underline text-sm text-stone-200 hover:text-white"}
          >
            Terms &amp; Conditions
          </button>
        )}
      </DialogTrigger>
      <DialogContent
        data-testid="terms-dialog"
        className="max-w-3xl bg-white p-0 rounded-sm border border-stone-200 overflow-hidden"
      >
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-stone-200">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#4A6076] mb-2">Legal — {COMPANY}</p>
          <DialogTitle className="font-serif-display text-3xl md:text-4xl text-stone-900 tracking-tight">
            Terms &amp; Conditions
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500 mt-1">
            Effective Date: {EFFECTIVE_DATE}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[70vh] px-8 pb-8">
          <div className="prose prose-stone max-w-none text-[14.5px] leading-relaxed text-stone-700 pt-6 space-y-6">
            <p>
              Welcome to <span className="font-medium text-stone-900">{COMPANY}</span>. These Terms &amp; Conditions
              (&ldquo;Terms&rdquo;) govern your access to and use of our website and services. By accessing or using this
              website, you agree to be bound by these Terms. If you do not agree with any part of these Terms, please
              discontinue use of the website.
            </p>

            {SECTIONS.map((s) => (
              <section key={s.heading} className="pt-4 border-t border-stone-100">
                <h3 className="font-serif-display text-xl text-stone-900 mb-3">{s.heading}</h3>
                <div className="text-stone-600">{s.body}</div>
              </section>
            ))}

            <p className="pt-6 border-t border-stone-100 text-xs text-stone-500 italic">
              Last Updated: {EFFECTIVE_DATE}. These Terms &amp; Conditions are provided as a general website template and
              should be reviewed by legal counsel to ensure compliance with the laws and regulations applicable to your
              business and jurisdiction.
            </p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
